import { createSelector, Selector } from 'reselect';

import { FeatureFlags } from '../../ts/FeatureFlags';
import { Helpers } from '../../ts/helpers/Helpers';
import { IUserInfo } from '../../ts/models/IUserInfo';
import { SiteRolesResource } from '../../ts/resources/SiteRoles';
import { Sites } from '../../ts/resources/Sites';
import { DisplayMode, IServerSessionInfo, ISessionInfo, IUserSessionInfo } from '../../ts/ServerApi/types/ISessionInfo';
import { ISiteNameWithRole } from '../../ts/ServerApi/types/ISiteNameWithRole';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { AppState } from '../state/AppState';

/*
 * This file contains selector functions that retrieve various pieces of the session info. Most of these used to exist
 * in ServerService.ts, but they're gradually moving over here as we need to access the same information in React/Redux code.
 * Eventually, this will be the single source of truth for this logic.
 * If you add a new selector to this file, please look in ServerService to see if it exists there too, and refactor the
 * relevant ServerService method to use the selector defined here.
 */


export const selectSessionInfo: Selector<AppState, ISessionInfo> = state => state.session && state.session.info;

export const selectSessionSite: Selector<AppState, ISiteNameWithRole> = state => selectSessionInfo(state) && state.session.info.site;
export const selectSessionUser: Selector<AppState, IUserSessionInfo> = state => selectSessionInfo(state) && state.session.info.user;
export const selectSessionServer: Selector<AppState, IServerSessionInfo> = state => selectSessionInfo(state) && state.session.info.server;

export namespace SessionSiteSelectors {

  function selectFromSessionSite<T>(innerSelector: Selector<ISiteNameWithRole, T>): Selector<AppState, T | undefined> {
    return state => Helpers.ifDefined(selectSessionSite(state), innerSelector);
  }

  export const isSessionSiteAdmin: Selector<AppState, boolean> = selectFromSessionSite(site => SiteRolesResource.isSiteAdmin(site.role));
  export const isVersioningEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.versioningEnabled);
  export const siteName: Selector<AppState, string> = selectFromSessionSite(site => site.name || '');
  export const siteRole: Selector<AppState, string> = selectFromSessionSite(site => site.role || '');
  export const siteUrlName: Selector<AppState, string> = selectFromSessionSite(site => site.urlName || '');
  export const siteId: Selector<AppState, string> = selectFromSessionSite(site => site.id || '');
  export const isLogoutEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.logoutEnabled);
  export const isNLPServiceEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.nlpserviceEnabled && SiteRolesResource.isSiteExplorerOrHigher(site.role));
  export const isVizRecommendationsEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => FeatureFlags.vizRecommendationsEnabled && site.vizRecommendationsEnabled);
  export const isSiteEARModeEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => Sites.isExtractEncryptionModeEnabled(site.extractEncryptionMode));
  export const extractEncryptionMode: Selector<AppState, string> = selectFromSessionSite(site => site.extractEncryptionMode);
  export const isDataAlertsEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.dataAlertsEnabled);
  export const isNamedSharingEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => FeatureFlags.namedSharingEnabled && site.siteNamedSharingSettings && site.siteNamedSharingSettings.namedSharingEnabled && site.role !== SiteRoles.Guest);
  export const isRequestAccessEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.requestAccessEnabled);
  export const isWebExtractionEnabled: Selector<AppState, boolean> = selectFromSessionSite(site => site.webExtractionEnabled && FeatureFlags.extractMoveToTheWebEnabled);
}


export namespace SessionUserSelectors {

  function selectFromSessionUser<T>(innerSelector: Selector<IUserSessionInfo, T>): Selector<AppState, T | undefined> {
    return state => Helpers.ifDefined(selectSessionUser(state), innerSelector);
  }

  export const canAuthorOnWeb: Selector<AppState, boolean> = selectFromSessionUser(user => user.canAuthorOnWeb);
  export const displayMode: Selector<AppState, DisplayMode> = selectFromSessionUser(user => user.displayMode);
  export const isSessionMultiSite: Selector<AppState, boolean> = selectFromSessionUser(user => user.numberOfSites > 1);
  export const isSessionServerAdmin: Selector<AppState, boolean> = selectFromSessionUser(user => user.hasServerDisplay);
  export const isSingleSiteServerAdmin: Selector<AppState, boolean> = state => isSessionServerAdmin(state) && !isSessionMultiSite(state);
  export const sessionUserId: Selector<AppState, string> = selectFromSessionUser(user => user.id);
  export const sessionUserName: Selector<AppState, string> = selectFromSessionUser(user => user.username);
  export const sessionDomainName: Selector<AppState, string> = selectFromSessionUser(user => user.domainName);
  export const canManageUsers: Selector<AppState, boolean> = selectFromSessionUser(user => user.canManageUsers);
}


export namespace SessionServerSelectors {

  function selectFromSessionServer<T>(innerSelector: Selector<IServerSessionInfo, T>): Selector<AppState, T | undefined> {
    return state => Helpers.ifDefined(selectSessionServer(state), innerSelector);
  }

  export const alertFailureCountThreshold: Selector<AppState, number> = selectFromSessionServer(server => server.alertFailureCountThreshold);
  export const alertLookBackDaysLimit: Selector<AppState, number> = selectFromSessionServer(server => server.alertLookBackDaysLimit);
  export const displaySchedulesInClientTimezone: Selector<AppState, boolean> = selectFromSessionServer(server => server.displaySchedulesInClientTimezone);
  export const displayScheduleDescriptionAsName: Selector<AppState, boolean> = selectFromSessionServer(server => server.displayScheduleDescriptionAsName);
  export const flowFailureThresholdForRunPrevention: Selector<AppState, number> = selectFromSessionServer(server => server.flowFailureThresholdForRunPrevention);
  export const ignoreExtractTaskPriority: Selector<AppState, boolean> = selectFromSessionServer(server => server.ignoreExtractTaskPriority);
  // Backend checks for 1) Feature Flags and 2) environment specific checkes. (e.g. online vs onprem) for populating server.extractEncryptionEnabled
  export const extractEncryptionEnabledOmniSwitch: Selector<AppState, boolean> = selectFromSessionServer(server => server.extractEncryptionEnabled);
  export const isSiteAdminSettingsEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => server.siteAdminSettingsEnabled);
  export const isOnlineEnvironment: Selector<AppState, boolean> = selectFromSessionServer(server => server.helpEdition === 'online');
  export const isAdminViewsEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => !server.adminViewsDisabled);
  export const offlineHelpEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => server.offlineHelpEnabled);
  export const isRecommendationsEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => server.recommendationsEnabled);
  export const isVizRecommendationsEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => FeatureFlags.vizRecommendationsEnabled && server.vizRecommendationsEnabled);
  export const siteAdminSettingsEnabled: Selector<AppState, boolean> = selectFromSessionServer(server => server.siteAdminSettingsEnabled);
}

export namespace CompositeSessionInfoSelectors {

  export const currentUserIsSiteOrServerAdminSelector: Selector<AppState, boolean> =
    state => SessionSiteSelectors.isSessionSiteAdmin(state) || SessionUserSelectors.isSessionServerAdmin(state);

  export function currentUserIsAdminOrOwnerSelector(owner: IUserInfo | null): Selector<AppState, boolean> {
    return createSelector(
      SessionUserSelectors.sessionUserId, currentUserIsSiteOrServerAdminSelector,
      (sessionUserId: string, isAdmin: boolean) => (owner && owner.id === sessionUserId) || isAdmin
    );
  }

  export const isLimitedUserVisibilityEnforced: Selector<AppState, boolean> = state => selectSessionInfo(state) && state.session.info.site &&
    Sites.isUserVisibilityLimited(state.session.info.site.userVisibility) && !(SessionUserSelectors.isSessionServerAdmin(state) || SiteRolesResource.isSiteCreatorOrHigher(state.session.info.site.role));
}
